import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Product, toThumbSrc } from '../../services/api';
import DiscountBadge from '../../components/DiscountBadge';

interface ProductShowcaseProps {
  featured: Product[];
  newArrivals: Product[];
}

const Section = styled.section`
  padding: clamp(80px, 12vw, 140px) 0;
  background: #ffffff;
  display: grid;
  gap: clamp(60px, 10vw, 90px);
`;

const Block = styled.div`
  display: grid;
  gap: clamp(24px, 5vw, 36px);
`;

const BlockHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: flex-end;
    justify-content: space-between;
  }
`;

const TitleGroup = styled.div`
  display: grid;
  gap: 8px;
`;

const Title = styled.h2`
  font-size: clamp(1.9rem, 3vw, 2.4rem);
  font-weight: 600;
  color: #2c2a29;
  letter-spacing: -0.01em;
`;

const Subtitle = styled.p`
  font-size: 1rem;
  color: rgba(44, 42, 41, 0.65);
  max-width: 540px;
  line-height: 1.6;
`;

const ViewAll = styled(Link)`
  font-size: 0.95rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #2f3a34;
  text-decoration: none;
  border-bottom: 1px solid rgba(47, 58, 52, 0.2);
  align-self: flex-start;
  padding-bottom: 4px;
  transition: color 0.2s ease;

  &:hover {
    color: #1f2823;
  }
`;

const Grid = styled.div`
  display: grid;
  gap: clamp(20px, 4vw, 32px);
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

const ProductCard = styled(Link)`
  position: relative;
  display: grid;
  gap: 18px;
  border-radius: 26px;
  overflow: hidden;
  background: #f7f3ee;
  color: inherit;
  text-decoration: none;
  border: 1px solid rgba(47, 58, 52, 0.08);
  box-shadow: 0 18px 30px rgba(37, 45, 40, 0.12);
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 28px 48px rgba(37, 45, 40, 0.18);
  }
`;

const ProductImage = styled.div<{ image?: string }>`
  position: relative;
  aspect-ratio: 4 / 3;
  background: ${({ image }) =>
    image
      ? `linear-gradient(180deg, rgba(18, 22, 19, 0.08), rgba(18, 22, 19, 0.35)), url('${image}') center/cover`
      : 'linear-gradient(155deg, rgba(47, 58, 52, 0.7), rgba(145, 120, 97, 0.7))'};
`;

const ProductContent = styled.div`
  display: grid;
  gap: 10px;
  padding: 0 22px 24px 22px;
`;

const ProductName = styled.h3`
  font-size: 1.15rem;
  font-weight: 600;
  color: #2c2a29;
  margin: 0;
  line-height: 1.4;
`;

const ProductDescription = styled.p`
  font-size: 0.95rem;
  line-height: 1.5;
  color: rgba(44, 42, 41, 0.65);
  margin: 0;
  min-height: 44px;
`;

const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
`;

const Price = styled.span<{ accent?: boolean }>`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${({ accent }) => (accent ? '#d49a73' : '#2f3a34')};
`;

const ComparePrice = styled.span`
  font-size: 0.95rem;
  color: rgba(44, 42, 41, 0.5);
  text-decoration: line-through;
`;

const EmptyState = styled.div`
  padding: 40px;
  border-radius: 24px;
  background: rgba(247, 243, 238, 0.8);
  border: 1px dashed rgba(47, 58, 52, 0.3);
  color: rgba(44, 42, 41, 0.6);
  text-align: center;
`;

const ProductShowcase: React.FC<ProductShowcaseProps> = ({ featured, newArrivals }) => {
  const renderProducts = (products: Product[]) => {
    if (!products.length) {
      return (
        <EmptyState>
          Våra favoriter uppdateras just nu. Titta gärna in igen eller kontakta oss för personlig rådgivning.
        </EmptyState>
      );
    }

    return products.map(product => {
      const image = product.images && product.images[0] ? toThumbSrc(product.images[0]) : undefined;
      const hasDiscount = product.has_active_discount && product.final_price !== undefined;
      const discountType =
        product.discount_type === 'percentage'
          ? 'percentage'
          : product.discount_type === 'fixed'
          ? 'fixed'
          : null;

      return (
        <ProductCard key={product.id} to={`/produkter/${product.slug || product.id}`}>
          <ProductImage image={image}>
            {hasDiscount && discountType && (
              <div style={{ position: 'absolute', top: 16, right: 16 }}>
                <DiscountBadge
                  discountPercentage={product.discount_percentage}
                  discountAmount={product.discount_amount}
                  discountType={discountType}
                  discountLabel={product.discount_label}
                  size="small"
                />
              </div>
            )}
          </ProductImage>
          <ProductContent>
            <ProductName>{product.name}</ProductName>
            {(product.short_description || product.description) && (
              <ProductDescription>
                {(product.short_description || product.description || '')
                  .replace(/<[^>]+>/g, '')
                  .slice(0, 80)}
                {(product.short_description || product.description || '').length > 80 ? '…' : ''}
              </ProductDescription>
            )}
            <PriceRow>
              <Price accent={hasDiscount}>
                {Math.round((product.final_price ?? product.price)).toLocaleString('sv-SE')} kr
              </Price>
              {hasDiscount && (
                <ComparePrice>{Math.round(product.price).toLocaleString('sv-SE')} kr</ComparePrice>
              )}
            </PriceRow>
          </ProductContent>
        </ProductCard>
      );
    });
  };

  return (
    <Section>
      <div className="container">
        <Block>
          <BlockHeader>
            <TitleGroup>
              <Title>Bästsäljare just nu</Title>
              <Subtitle>
                Våra mest älskade produkter – perfekta som gåva eller för att förädla hemmets hjärtan.
              </Subtitle>
            </TitleGroup>
            <ViewAll to="/produkter">Visa alla produkter</ViewAll>
          </BlockHeader>
          <Grid>{renderProducts(featured)}</Grid>
        </Block>

        <Block>
          <BlockHeader>
            <TitleGroup>
              <Title>Nyheter i butiken</Title>
              <Subtitle>
                Färska tillskott från våra favoritvarumärken. Upptäck nya dofter, textilier och buketter.
              </Subtitle>
            </TitleGroup>
            <ViewAll to="/produkter?sort=nyheter">Se senaste</ViewAll>
          </BlockHeader>
          <Grid>{renderProducts(newArrivals)}</Grid>
        </Block>
      </div>
    </Section>
  );
};

export default ProductShowcase;
